import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { AuthService } from '@auth0/auth0-angular';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { ConfigurationService } from '@sondermind/configuration';
import { EmptyURIValue, encodeSpecialCharacters, URIPrefix } from '@sondermind/data-access/directory-link';
import { PhoneAsyncValidator } from '@sondermind/data-access/phone-validator';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { IAppointmentSlot, ProviderCalendarStore } from '@sondermind/data-access/public-facing/provider-calendar';
import { IState, StatesServedStore } from '@sondermind/data-access/public-facing/states-served';
import { ProviderCalendarComponent } from '@sondermind/features/public-facing/provider-calendar';
import { BOOK_INTENT_PARAM, BookIntentParam } from '@sondermind/host-auth';
import { breakpointXs } from '@sondermind/iris-design-tokens/design-tokens';
import {
  COMMON,
  OPC_EXPERIMENT_CONTROL_VARIANT,
  OPC_EXPERIMENT_TEST_VARIANT,
} from '@sondermind/constants';
import {
  IrisButtonContainerAlignments,
  IrisButtonContainerDesktopLayouts,
  IrisButtonContainerMobileLayouts,
  IrisButtonModule,
  IrisButtonTypes
} from '@sondermind/iris/button';
import { IrisCardModule } from '@sondermind/iris/card';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { DropdownOption, IrisDropdownModule } from '@sondermind/iris/dropdown';
import { IrisEmptyStateModule } from '@sondermind/iris/empty-state';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisModalRef } from '@sondermind/iris/modal';
import { IrisButtonTarget } from '@sondermind/iris/shared';
import { IrisTextInputModule } from '@sondermind/iris/text-input';
import { SegmentService } from '@sondermind/segment';
import { BirthdayValidator } from '@sondermind/utilities/forms';
import {
  IInsurance,
  InsuranceType,
  isTricare,
  NoInsuranceOptionValue
} from '@sondermind/utilities/models-provider-network-company';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { SessionPreference } from '@sondermind/utilities/models-session';
import { RequestedTimeSlotPipe } from '@sondermind/utilities/pipes/requested-time-slot.pipe';
import { USStateAbbrevMap } from '@sondermind/utilities/states-list';
import { SharedMasks } from '@sondermind/utilities/text-masks';
import { WindowRefService } from '@sondermind/utilities/window';
import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';

import {
  FEATURE_FLAG_SERVICE,
  FeatureFlagKey,
  FeatureFlagVariant,
  IFeatureFlagService,
} from '@sondermind/data-access/feature-flags';
import { RequestSessionExitClickTarget } from '../models/enums/request-session-exits.enum';
import {
  IQuickCheckoutApiResponse,
  IQuickCheckoutForm,
  IQuickCheckoutFormValues,
  IQuickCheckoutReferralRequestResponse,
  PaymentTypes
} from '../models/quick-checkout-form.model';
import {
  IQuickCheckoutModalConfigData,
  IQuickCheckoutModalConfigProvider
} from '../models/quick-checkout-modal-config.model';
import { QuickCheckoutStore } from '../stores/quick-checkout.store';

export enum QuickCheckoutState {
  FORM = 'form',
  ERROR = 'error',
  LOADING = 'loading',
  SELECT_NEW_TIME = 'select-new-time',
  SUBMITTED = 'submitted'
}

export enum QuickCheckoutDirectoryLinkButtons {
  STATE = 'show other states',
  INSURANCE = 'show other insurances'
}
export const PSYCHIATRIST_EDUCATION_LEVELS = ['MD', 'DO', 'RN'];

export const HEARD_FROM_OPTIONS: Array<DropdownOption<string>> = [
  { name: 'EAP/Work', value: 'eap or work' },
  { name: 'Facebook', value: 'facebook' },
  { name: 'Instagram', value: 'instagram' },
  { name: 'Insurance Directory', value: 'insurance directory' },
  { name: 'Internet Search', value: 'internet_search' },
  { name: 'Medical Provider Referral', value: 'medical provider' },
  { name: 'Podcast', value: 'podcast' },
  { name: 'Psychology Today', value: 'psychology today' },
  { name: 'Radio', value: 'radio' },
  { name: 'Sign or Billboard', value: 'billboard' },
  { name: 'TikTok', value: 'tiktok' },
  { name: 'TV', value: 'television' },
  { name: 'WebMD', value: 'webmd' },
  { name: 'Word of Mouth', value: 'word' },
  { name: 'YouTube', value: 'youtube' }
];

export const TIME_SLOT_UNAVAILABLE_ERROR_CODE = 422;
export const TIME_SLOT_UNAVAILABLE_ERROR_MESSAGE = 'slot is unavailable';

function isAssignedOpcVariant(
  variant: FeatureFlagVariant<string> | null,
): variant is FeatureFlagVariant<string> {
  return variant?.key === OPC_EXPERIMENT_TEST_VARIANT
    || variant?.key === OPC_EXPERIMENT_CONTROL_VARIANT;
}

@Component({
  selector: 'quick-checkout',
  host: {
    '[attr.data-test]': "'quick-checkout-container'"
  },
  imports: [
    CommonModule,
    IrisCardModule,
    IrisCheckboxModule,
    IrisDropdownModule,
    IrisEmptyStateModule,
    IrisFormFieldModule,
    IrisIconComponent,
    IrisTextInputModule,
    IrisButtonModule,
    ProviderCalendarComponent,
    ReactiveFormsModule,
    RequestedTimeSlotPipe,
  ],
  templateUrl: './quick-checkout.component.html',
  styleUrls: ['./quick-checkout.component.scss']
})
export class QuickCheckoutComponent implements OnInit {
  initialState: string = '';
  initialPaymentPref: string = '';
  initialPncId: string = '';
  referringUrl?: string;
  isMobile$!: Observable<boolean>;
  provider!: IQuickCheckoutModalConfigProvider;
  requestedSessionPreferences!: ISessionRequestPreferences;

  useAuth0: boolean = false;
  showSignInToBook: boolean = false;
  isPsychiatrist: boolean = false;
  quickCheckoutState: QuickCheckoutState = QuickCheckoutState.LOADING;
  quickCheckoutFormGroup: FormGroup<IQuickCheckoutForm> = new FormGroup({
    commPrefs: new FormGroup({
      commPrefsPhone: new FormControl(true),
      commPrefsSms: new FormControl(true)
    }, Validators.required),
    contactSmsAllowed: new FormControl<boolean | null>(true),
    birthDate: new FormControl<string | null>(null, [
      Validators.required,
      BirthdayValidator.validate
    ]),
    firstName: new FormControl<string | null>(null, Validators.required),
    lastName: new FormControl<string | null>(null, Validators.required),
    email: new FormControl<string | null>(null, [Validators.required, EmailValidator.format]),
    insurance: new FormControl<number | null>({ value: null, disabled: true }),
    location: new FormControl<string | null>(null, Validators.required),
    paymentType: new FormControl<PaymentTypes | null>({ value: null, disabled: true }, Validators.required),
    phone: new FormControl<string | null>(
      null,
      {
        updateOn: 'blur',
        validators: [Validators.required, PhoneValidator.format],
        asyncValidators: [this.phoneAsyncValidator.validate.bind(this.phoneAsyncValidator)]
      }
    ),
    activeDuty: new FormControl<boolean | null>(null),
    heardFrom: new FormGroup({
      source: new FormControl<string | null>(null),
      details: new FormControl<string | null>(null)
    })
  });

  heardFromOptions: Array<DropdownOption<string>> = this.buildHeardFromOptions();
  shouldShowHeardFromDetails = false;
  heardFromDetailsHeading = '';

  insuranceControlSubscription: Subscription | null = null;
  isLastNameControlPristine = true;
  isFormValid = false;
  /** Angular doesn't run validation on disabled fields.
   * This mechanism keeps the submit button disabled when any field in the form is disabled. */
  isAnyFieldDisabled = true;

  allAcceptedInsurancesControl: FormControl<number | null> = new FormControl<number | null>(null);

  phoneErrorMessage = 'Please enter a valid phone number.';

  irisButtonAlignment = IrisButtonContainerAlignments;
  irisButtonTypeSecondary = IrisButtonTypes.SECONDARY;
  irisButtonTargetBlank = IrisButtonTarget.BLANK;
  iconSizes = IconSizes;
  irisButtonContainerLayout = IrisButtonContainerMobileLayouts.SIDEBYSIDE;
  irisButtonContainerDesktopLayout = IrisButtonContainerDesktopLayouts.FULLWIDTH;
  quickCheckoutDirectoryLinkButtons = QuickCheckoutDirectoryLinkButtons;
  sessionTypePreference = SessionPreference;
  supportPhoneDisplay = COMMON.SUPPORT_PHONE_THERAPY_DISPLAY;

  showNotLicensedInStateMessage = false;

  private isNotLicensedThereMessageSubj$ = new BehaviorSubject<string>(
    `Sorry, ${this.provider?.firstName} is not licensed there`
  );
  isNotLicensedThereMessage$ = this.isNotLicensedThereMessageSubj$.asObservable();

  private destroyed$ = new Subject<void>();

  masks = {
    birthdayMask: SharedMasks['birthdayMask'].ngxMask,
    phoneMask: SharedMasks['phoneMask'].ngxMask
  };

  stateOptions: Array<DropdownOption<string>> = [];
  tricareOptions: Array<DropdownOption<boolean>> = [
    { name: 'Active Duty service member', value: true },
    { name: 'Retiree or family member', value: false }
  ];

  acceptedInsurances: Array<DropdownOption<number>> = [];
  allInsuranceOptions: Array<DropdownOption<number>> = [];
  appointmentSlots: IAppointmentSlot[] = [];
  availablePaymentTypes: Array<DropdownOption<string>> = [];
  insurances: IInsurance[] = [];
  showInsurancePlans: boolean = false;
  showTricareStatus: boolean = false;
  showAllInsuranceOptions: boolean = false;
  insuranceFormLabel: string = '';

  linkToProviderDirectory: string = '';

  insurancesActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  paymentTypesActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  haveInitialFormValuesBeenSet: boolean = false;

  constructor(
    private irisModalRef: IrisModalRef<QuickCheckoutComponent, IQuickCheckoutModalConfigData, string>,
    private phoneAsyncValidator: PhoneAsyncValidator,
    private pncStore: ProviderNetworkCompanyStore,
    private quickCheckoutStore: QuickCheckoutStore,
    private statesServedStore: StatesServedStore,
    private segmentService: SegmentService<SondermindEvents>,
    private configurationService: ConfigurationService,
    private providerCalendarStore: ProviderCalendarStore,
    private bpObserver: BreakpointObserver,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private windowRef: WindowRefService,
  ) { }

  ngOnInit(): void {
    const { data } = this.irisModalRef.config;
    if (data) {
      this.provider = data.provider;
      this.initialState = data.initialState;
      this.initialPaymentPref = data.initialPaymentPref;
      this.initialPncId = data.initialPncId;
      this.referringUrl = data.referringUrl;
      this.requestedSessionPreferences = data.requestedSessionPreferences;
    }

    // MER-4944: OPC retest experiment exposure + variant Started event. Fired at
    // the top of ngOnInit so a downstream crash still counts as an exposure
    // (ITT-preserving). Both are skipped when opened by `?internal=true` since
    // support / enterprise-card coordinators are not part of the experiment
    // population — firing Started for them would break exposure-symmetry with
    // the OPC arm.
    if (!data?.skipOpcExperiment) {
      this.trackOpcExperimentExposure();
      this.sendSegmentEvent(SondermindEvent.QuickCheckoutStarted, {
        personaProviderId: this.provider?.id,
      });
    }

    this.determineSignupStyle();
    this.checkProviderType(this.provider.educationLevel);
    this.isMobile$ = this.isMobileChanges$();
    // Only allow the form to be submitted out once
    // If we detect the form has already been successfully submitted,
    // show the confirmation and don't do set up
    if (this.quickCheckoutStore.checkIsSubmittedState()) {
      this.quickCheckoutState = QuickCheckoutState.SUBMITTED;

      this.requestedSessionPreferences = this.quickCheckoutStore.getSubmittedSessionRequestPreferences()
        ?? this.requestedSessionPreferences;
      this.resizeModalToFitContent();
    } else {
      this.setUpSubscriptions();
      this.getStateOptions$().subscribe(() => {
        this.quickCheckoutState = QuickCheckoutState.FORM;
      });

      combineLatest([
        this.paymentTypesActive,
        this.insurancesActive
      ]).subscribe(([types, insurance]) => {
        if (types && insurance && !this.haveInitialFormValuesBeenSet) {
          // Wait for the new payment types to populate into the dropdown options
          // otherwise there are no dropdown options and the payment type will not register
          setTimeout(() => {
            this.handleIncomingInsurance();
          }, 1);

          this.haveInitialFormValuesBeenSet = true;
        }
      });
    }
  }

  private shuffleArray<T>(originalArray: T[]): T[] {
    const result = originalArray;
    let currentIndex = result.length - 1;
    while (currentIndex > 0) {
      const randomIndex = Math.floor(Math.random() * (currentIndex + 1));
      [result[currentIndex], result[randomIndex]] = [result[randomIndex], result[currentIndex]];
      currentIndex -= 1;
    }
    return result;
  }

  private buildHeardFromOptions(): Array<DropdownOption<string>> {
    const options = this.shuffleArray(HEARD_FROM_OPTIONS);

    // Add the "other" option to the end of the array
    options.push({ name: 'Other', value: 'other' });

    return options;
  }

  private determineSignupStyle(): void {
    // Support team and enterprise card coordinators use directory to help onboard clients
    // and need access to the non-Auth0 signup flow
    // https://sondermind-jira.atlassian.net/browse/MER-3306
    // https://sondermind-jira.atlassian.net/browse/MER-3493
    if (this.activatedRoute.snapshot.queryParamMap.get('internal') === 'true') return;

    combineLatest([
      this.featureFlagService.isEnabled$(FeatureFlagKey.QUICK_CHECKOUT_AUTH0),
      this.featureFlagService.isEnabled$(FeatureFlagKey.QUICK_CHECKOUT_SIGN_IN_TO_BOOK)
    ]).pipe(
      takeUntil(this.destroyed$)
    ).subscribe(([useAuth0, signInToBook]) => {
      if (useAuth0) {
        this.useAuth0 = useAuth0;
        if (this.quickCheckoutFormGroup.contains('email')) {
          (this.quickCheckoutFormGroup as FormGroup).removeControl('email');
        }
      }
      // The sign-in link uses Auth0 login directly, independent of the QUICK_CHECKOUT_AUTH0
      // signup experiment, so it is gated on its own flag only.
      this.showSignInToBook = signInToBook;
    });
  }

  private getStateOptions$(): Observable<boolean> {
    return this.statesServedStore.stateList$().pipe(
      tap((array: IState[]) => {
        this.stateOptions = array.map((state: IState) => ({ name: state.name, value: state.abbreviation }));
        // Initialize form with the provided state (either query param state or provider practice state)
        if (this.initialState
          && this.initialState !== ''
          && array.find((state) => state.abbreviation === this.initialState)
        ) {
          this.quickCheckoutFormGroup.controls.location.setValue(this.initialState);
          this.quickCheckoutFormGroup.controls.location.updateValueAndValidity();
        }
      }),
      take(1),
      map(() => true)
    );
  }

  private checkProviderType(educationLevel: string) {
    this.isPsychiatrist = PSYCHIATRIST_EDUCATION_LEVELS.includes(educationLevel);
  }

  // Initialize the form with provided insurance from query params (if it exists)
  private handleIncomingInsurance(): void {
    if (this.initialPaymentPref) {
      // Insurances from the directory page query params are of type InsuranceType
      switch (this.initialPaymentPref) {
        case InsuranceType.INSURANCE:
          if (this.isAnAcceptedPaymentType(PaymentTypes.INSURANCE)) {
            this.quickCheckoutFormGroup.controls.paymentType.setValue(PaymentTypes.INSURANCE);
          }
          break;
        case InsuranceType.MEDICARE:
          if (this.isAnAcceptedPaymentType(PaymentTypes.MEDICARE)) {
            this.quickCheckoutFormGroup.controls.paymentType.setValue(PaymentTypes.MEDICARE);
          }
          break;
        case InsuranceType.MEDICAID:
          if (this.isAnAcceptedPaymentType(PaymentTypes.MEDICAID)) {
            this.quickCheckoutFormGroup.controls.paymentType.setValue(PaymentTypes.MEDICAID);
          }
          break;
        case InsuranceType.EAP:
          if (this.isAnAcceptedPaymentType(PaymentTypes.EAP)) {
            this.quickCheckoutFormGroup.controls.paymentType.setValue(PaymentTypes.EAP);
          }
          break;
        case InsuranceType.CASH:
          // cash pay pncId can be either CASH_PAY or NOT_SURE, do not prefill if not sure
          if (this.compareStrings(this.initialPncId, NoInsuranceOptionValue.CASH_PAY.toString())) {
            this.quickCheckoutFormGroup.controls.paymentType.setValue(PaymentTypes.CASH);
          }
          break;
        default:
      }

      this.quickCheckoutFormGroup.controls.paymentType.updateValueAndValidity();

      if (this.initialPncId) {
        const pncId = Number(this.initialPncId);
        if (this.provider.acceptedInsuranceIds.includes(pncId)) {
          this.quickCheckoutFormGroup.controls.insurance.setValue(pncId);
          this.quickCheckoutFormGroup.controls.insurance.updateValueAndValidity();
        }
      }
    }
  }

  private setUpSubscriptions(): void {
    this.setUpLocationSubscription();
    this.setUpPaymentPreferenceSubscription();
    this.setupInsuranceSelectionSubscription();
    this.setupActiveDutySubscription();
    this.setUpAllInsuranceOptionsSubscription();
    this.setUpPersonalInfoSubscription();
    this.setUpFormCompleteSubscription();
    this.setUpHeardFromSubscription();
  }

  private setUpHeardFromSubscription(): void {
    this.quickCheckoutFormGroup.controls.heardFrom.controls.source.valueChanges.pipe(
      takeUntil(this.destroyed$),
    ).subscribe((source) => {
      if (source && this.hasHeardFromDetails(source)) {
        this.heardFromDetailsHeading = this.getHeardFromDetailsHeading(source ?? '');
        this.shouldShowHeardFromDetails = true;
      } else {
        this.shouldShowHeardFromDetails = false;
        this.quickCheckoutFormGroup.controls.heardFrom.controls.details.reset();
        this.quickCheckoutFormGroup.controls.heardFrom.controls.details.updateValueAndValidity();
      }
    });
  }
  private hasHeardFromDetails(item: string): boolean {
    return ['other', 'podcast', 'tiktok', 'facebook', 'youtube', 'instagram'].includes(item);
  }

  private getHeardFromDetailsHeading(item: string): string {
    if (item === 'other') return 'Please enter how you heard about us.';
    if (item === 'podcast') return 'Which podcast?';
    return 'If you heard about us from someone you follow, enter their name here.';
  }

  /**
   * Location is a substantial change for this form, causing a somewhat involved series of UI changes:
   *
   * 1. We let the user select location from all of the states we offer. If the provider does not have a license there,
   * we route them to the provider-directory where they can find providers in that state.
   *
   * 2. Payment preference control is dependent on location and should be disabled when a location hasn't been selected.
   * We fetch new insurances every time a new location is selected (from a store, which caches results).
   */
  private setUpLocationSubscription(): void {
    this.quickCheckoutFormGroup.controls.location.valueChanges.pipe(
      filter(
        (locationAbbreviation: string | null) => locationAbbreviation !== null && locationAbbreviation !== undefined
      ),
      tap((locationAbbreviation: string) => {
        // disable the payment type control
        this.quickCheckoutFormGroup.controls.paymentType.disable({ emitEvent: false });

        this.sendLocationChangedSegmentEvent(locationAbbreviation);
      }),
      switchMap((locationAbbreviation) => this.pncStore.getInsuranceOptionsByState$({ state: locationAbbreviation }).pipe(
        // nested pipe is necessary to have access to both `locationAbbreviation` and `insurances`.
        tap((insurances) => {
          this.updateAvailablePaymentTypes(insurances);

          const isProviderLicensedInState = this.isProviderLicensedInState(locationAbbreviation);
          this.showNotLicensedInStateMessage = !isProviderLicensedInState;

          if (isProviderLicensedInState) {
            this.enableAllControls();
            this.insurancesActive.next(true);
          } else {
            this.handleProviderNotLicensedInState(locationAbbreviation);
          }
        })
      )),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  private isProviderLicensedInState(locationAbbreviation: string): boolean {
    const stateLongName: string = USStateAbbrevMap[locationAbbreviation] ?? '';
    return this.provider.credentialedStates.includes(stateLongName);
  }

  private handleProviderNotLicensedInState(locationAbbreviation: string): void {
    // change 'Sorry, Dominique is not licensed in Vermont' message to the provider name and selected state
    this.publishNotLicensedThereMessage(locationAbbreviation);

    // append the state abbreviation to the provider directory URL so it filters appropriately
    this.linkToProviderDirectory = this.buildDirectoryLink(locationAbbreviation);

    // disable controls until the location is updated again.
    this.disableNonLocationControls();
  }

  private sendLocationChangedSegmentEvent(location: string): void {
    const isProviderCredentialedInState = this.provider.credentialedStates.includes(location);
    this.sendSegmentEvent(
      SondermindEvent.PslQuickCheckoutFormLocationChanged,
      {
        selectedState: location,
        isProviderCredentialedInState
      }
    );
  }

  private disableNonLocationControls(): void {
    this.quickCheckoutFormGroup.disable({ emitEvent: false });
    this.quickCheckoutFormGroup.controls.location.enable({ emitEvent: false });
    this.isAnyFieldDisabled = true;
  }

  private disableNonActiveDutyControls(): void {
    this.quickCheckoutFormGroup.disable({ emitEvent: false });
    this.quickCheckoutFormGroup.controls.location.enable({ emitEvent: false });
    this.quickCheckoutFormGroup.controls.paymentType.enable({ emitEvent: false });
    this.quickCheckoutFormGroup.controls.insurance.enable({ emitEvent: false });
    this.quickCheckoutFormGroup.controls.activeDuty.enable({ emitEvent: false });
    this.isAnyFieldDisabled = true;
  }

  private enableAllControls(): void {
    this.quickCheckoutFormGroup.enable({ emitEvent: false });
    this.isAnyFieldDisabled = false;
  }

  private updateAvailablePaymentTypes(insurances: IInsurance[]): void {
    this.availablePaymentTypes = [];
    const paymentTypeSet: Set<string> = new Set();
    // First, filter by the insurances that the provider accepts,
    // so we don't allow users to select payment types that don't have any plan options
    const acceptedInsurances = insurances.filter((i) => this.provider.acceptedInsuranceIds.includes(i.pncId));

    acceptedInsurances.forEach((insurance) => {
      if (insurance.isEap) {
        paymentTypeSet.add(PaymentTypes.EAP);
      }
      if (insurance.isMedicare) {
        paymentTypeSet.add(PaymentTypes.MEDICARE);
      }
      if (insurance.isPrivateInsurance) {
        paymentTypeSet.add(PaymentTypes.INSURANCE);
      }
      // Do not show medicaid options for psychiatrists
      if (insurance.isMedicaid && !this.isPsychiatrist) {
        paymentTypeSet.add(PaymentTypes.MEDICAID);
      }
    });
    paymentTypeSet.forEach((name) => this.availablePaymentTypes.push({ name, value: name }));
    this.availablePaymentTypes.push({ name: PaymentTypes.CASH, value: PaymentTypes.CASH });

    this.insurances = insurances;
    this.paymentTypesActive.next(true);

    this.updatePaymentTypeBasedOnAvailability();
  }

  private updatePaymentTypeBasedOnAvailability(): void {
    const paymentTypeControl = this.quickCheckoutFormGroup.controls.paymentType;
    if (this.availablePaymentTypes.find((option) => option.value === paymentTypeControl.value)) {
      // trigger subscription to update available "insurance" options
      paymentTypeControl.setValue(paymentTypeControl.value);
    } else {
      paymentTypeControl.reset();
    }
  }

  private setUpPaymentPreferenceSubscription(): void {
    this.quickCheckoutFormGroup.controls.paymentType.valueChanges.pipe(
      tap((paymentType) => {
        if (paymentType) {
          this.sendPaymentPreferenceSegmentEvent(paymentType);
          this.updateInsuranceControlByPaymentType(paymentType);
        } else {
          this.resetInsuranceControl();
        }
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  private setupInsuranceSelectionSubscription(): void {
    const { insurance } = this.quickCheckoutFormGroup.controls;
    insurance.valueChanges.pipe(
      distinctUntilChanged(),
      tap((pncId) => {
        const selection = this.insurances.find((option) => pncId === option.pncId);
        this.showTricareStatus = isTricare(selection);
        this.updateActiveDutyValidation();
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  private updateActiveDutyValidation(): void {
    const { activeDuty } = this.quickCheckoutFormGroup.controls;
    if (this.showTricareStatus) {
      activeDuty.addValidators(Validators.required);
      activeDuty.updateValueAndValidity();
      return;
    }

    activeDuty.removeValidators(Validators.required);
    if (activeDuty.value === true) {
      this.enableAllControls();
    }
    activeDuty.patchValue(null, { emitEvent: false });
    activeDuty.updateValueAndValidity();
  }

  private setupActiveDutySubscription(): void {
    const { activeDuty } = this.quickCheckoutFormGroup.controls;
    activeDuty.valueChanges.pipe(
      filter((x) => x !== null && x !== undefined),
      tap((isActiveDuty) => {
        isActiveDuty ? this.disableNonActiveDutyControls() : this.enableAllControls();
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  private resetInsuranceControl(): void {
    const { insurance } = this.quickCheckoutFormGroup.controls;
    insurance.reset();
    insurance.setValue(null);
  }

  private sendPaymentPreferenceSegmentEvent(selectedPaymentMethod: PaymentTypes): void {
    this.sendSegmentEvent(
      SondermindEvent.PslQuickCheckoutFormPaymentMethodChanged,
      { selectedPaymentMethod }
    );
  }

  private updateInsuranceControlByPaymentType(paymentType: PaymentTypes): void {
    const { insurance } = this.quickCheckoutFormGroup.controls;

    if (this.insuranceControlSubscription) {
      this.insuranceControlSubscription.unsubscribe();
    }

    this.resetInsuranceControl();

    const isAcceptedPaymentType = this.availablePaymentTypes.find((pt) => pt.value === paymentType);
    if (isAcceptedPaymentType && paymentType !== PaymentTypes.CASH) {
      this.showInsurancePlans = true;
      insurance.enable();
      insurance.addValidators(Validators.required);

      const filterByType = this.filterByInsuranceType(paymentType);
      this.insuranceFormLabel = this.getInsurancePlanLabel(paymentType);

      this.allInsuranceOptions = this.insurances
        .filter((i) => filterByType(i))
        .map((accepted) => ({ name: accepted.title, value: accepted.pncId }));

      this.acceptedInsurances = this.allInsuranceOptions
        .filter((i) => this.provider.acceptedInsuranceIds.includes(i.value));

      this.insuranceControlSubscription = insurance.valueChanges.subscribe((pncId) => {
        const selection = this.acceptedInsurances.find((option) => option.value === pncId);
        this.sendInsurancePlanSegmentEvent(selection?.name);
      });
    } else {
      this.showInsurancePlans = false;
      insurance.disable();
      insurance.removeValidators(Validators.required);
    }
  }

  private sendInsurancePlanSegmentEvent(selectedPaymentPlan: string | undefined) {
    if (selectedPaymentPlan) {
      this.sendSegmentEvent(
        SondermindEvent.PslQuickCheckoutFormPaymentPlanChanged,
        { selectedPaymentPlan }
      );
    }
  }

  private setUpAllInsuranceOptionsSubscription(): void {
    this.allAcceptedInsurancesControl.valueChanges.pipe(
      tap((insurance) => {
        if (insurance) {
          const stateAbbreviation = this.quickCheckoutFormGroup.controls.location.value ?? '';
          this.linkToProviderDirectory = this.buildDirectoryLink(stateAbbreviation, insurance);
        }
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  /** Track the editing of personal info, to see if users are bouncing away
   * in this section of the form. This will track when the user first enters any value
   * into the last name field */
  private setUpPersonalInfoSubscription(): void {
    const lastNameControl = this.quickCheckoutFormGroup.controls.lastName;
    lastNameControl.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      // Control is turning dirty for the first time
      if (this.isLastNameControlPristine && lastNameControl.dirty) {
        this.isLastNameControlPristine = false;
        this.sendPersonalInfoSegmentEvent();
      }
    });
  }

  private setUpFormCompleteSubscription(): void {
    const validStatus = 'VALID';

    this.quickCheckoutFormGroup.statusChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((status) => {
      // Track only when it's turning valid after not being valid
      if (status === validStatus && !this.isFormValid) {
        this.isFormValid = true;
        this.sendFormCompleteSegmentEvent();
      }

      this.isFormValid = status === validStatus;
    });
  }

  private sendPersonalInfoSegmentEvent(): void {
    this.sendSegmentEvent(
      SondermindEvent.PslQuickCheckoutFormPersonalInfoChanged,
      { description: 'Last name was edited for the first time.' }
    );
  }

  private sendFormCompleteSegmentEvent(): void {
    this.sendSegmentEvent(
      SondermindEvent.PslQuickCheckoutFormAllFieldsCompleted,
      {}
    );
  }

  private sendSubmitFormSegmentEvent(requestedSessionPreferences: ISessionRequestPreferences): void {
    let sessionRequestDetails = null;

    if (!!requestedSessionPreferences?.startTime && !!requestedSessionPreferences?.endTime) {
      const timeDiffInSeconds = Math.floor(
        (requestedSessionPreferences.startTime.getTime() - new Date().getTime()) / 1000
      );
      const hoursFromRequestSubmission = Math.floor(timeDiffInSeconds / 3600);
      sessionRequestDetails = {
        startTime: requestedSessionPreferences.startTime.toLocaleString('en-US', { timeZoneName: 'short' }),
        endTime: requestedSessionPreferences.endTime.toLocaleString('en-US', { timeZoneName: 'short' }),
        sessionType: this.requestedSessionPreferences.sessionType ?? '',
        hoursFromRequestSubmission
      };
    }

    if (this.useAuth0) {
      this.sendSegmentEvent(
        SondermindEvent.PslQuickCheckoutRedirectedToAuth0,
        { personaProviderId: this.provider.id }
      );
    } else {
      this.sendSegmentEvent(
        SondermindEvent.QuickCheckoutFormSubmitted,
        {
          personaProviderId: this.provider.id,
          sessionRequested: !!sessionRequestDetails,
          sessionRequestDetails
        }
      );
    }
  }

  /**
   * This publishes the 'Sorry Dominique is not licensed in Ohio' message, which changes dependent on state selected
   */
  private publishNotLicensedThereMessage(locationAbbreviation: string): void {
    let message: string;

    if (locationAbbreviation === '') {
      // if the location abbreviation is a blank string, we don't have anything to publish.
      message = `Sorry, ${this.provider.firstName} is not licensed there.`;
    } else if (locationAbbreviation === 'DC') {
      // if it's DC, that's a special case, so we write the message
      message = `Sorry, ${this.provider.firstName} is not licensed in DC.`;
    } else {
      const selectedStateLongName = USStateAbbrevMap[locationAbbreviation];
      message = `Sorry, ${this.provider.firstName} is not licensed in ${selectedStateLongName}.`;
    }

    this.isNotLicensedThereMessageSubj$.next(message);
  }

  private isMobileChanges$(): Observable<boolean> {
    return this.bpObserver
      .observe([`(max-width: ${breakpointXs})`])
      .pipe(
        takeUntil(this.destroyed$),
        map((state: BreakpointState) => state.matches)
      );
  }

  onBack(): void {
    this.quickCheckoutState = QuickCheckoutState.FORM;
  }

  onCancel(): void {
    this.irisModalRef.close();
    this.sendSegmentEvent(
      SondermindEvent.QuickCheckoutFormExited,
      { exitedBy: RequestSessionExitClickTarget.CANCEL_BUTTON }
    );
  }

  /**
   * Returning users who already have an account click "Sign in" instead of filling out the form.
   * We send them to Auth0 to log in (not sign up) and encode the provider + requested timeslot as
   * query params on the redirect_uri. Auth0 redirects to client-portal (a different domain), which
   * reads these params post-login and opens the direct-booking modal for the same provider.
   */
  onClickSignIn(): void {
    this.sendSegmentEvent(
      SondermindEvent.PslQuickCheckoutSignInClicked,
      { personaProviderId: this.provider.id }
    );

    const params = this.buildBookingIntentParams(this.requestedSessionPreferences);
    const redirectUri = `${this.configurationService.env.clientPortalBase}?${params.toString()}`;

    /* eslint-disable @typescript-eslint/naming-convention */
    this.authService.loginWithRedirect({
      authorizationParams: {
        scope: 'offline_access',
        screen_hint: 'login',
        redirect_uri: redirectUri,
        eeNamespace: this.configurationService.env.eeNamespace
      }
    });
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  /**
   * Builds the booking-intent query params carried across the cross-domain Auth0 login.
   * Only the provider id and (when a slot was picked) the slot fields are encoded; client-portal
   * hydrates the full provider from the id. Dates are ISO strings so they survive URL transport.
   */
  private buildBookingIntentParams(prefs: ISessionRequestPreferences): URLSearchParams {
    const params = new URLSearchParams();
    params.set(BOOK_INTENT_PARAM, 'true');
    params.set(BookIntentParam.PROVIDER_ID, String(this.provider.id));

    if (prefs?.startTime && prefs?.endTime) {
      params.set(BookIntentParam.START, new Date(prefs.startTime).toISOString());
      params.set(BookIntentParam.END, new Date(prefs.endTime).toISOString());
      if (prefs.sessionType) {
        params.set(BookIntentParam.SESSION_TYPE, prefs.sessionType);
      }
    }

    return params;
  }

  onClickSubmit(requestedSessionPreferences: ISessionRequestPreferences): void {
    this.quickCheckoutState = QuickCheckoutState.LOADING;
    this.requestedSessionPreferences = requestedSessionPreferences; // May have changed since the modal first opened

    const isSessionTimeRequested = !!(requestedSessionPreferences?.startTime
      && requestedSessionPreferences?.endTime);
    const formValues: IQuickCheckoutFormValues = {
      ...this.quickCheckoutFormGroup.getRawValue(),
      personaProviderId: this.provider.id
    };

    this.sendSubmitFormSegmentEvent(requestedSessionPreferences);
    if (this.useAuth0) {
      this.submitAuth0(formValues, isSessionTimeRequested);
    } else {
      this.submitNonAuth0(formValues, isSessionTimeRequested);
    }
  }

  private submitAuth0(formValues: IQuickCheckoutFormValues, isSessionTimeRequested: boolean): void {
    combineLatest([
      this.quickCheckoutStore.createReferralRequest$(
        formValues,
        this.requestedSessionPreferences,
        this.windowRef.nativeWindow?.location?.href ?? '',
        this.referringUrl
      ).pipe(
        catchError((error: HttpErrorResponse) => this.handleSubmitError(error, isSessionTimeRequested))
      ),
      from(this.segmentService.anonymousId())
    ]).subscribe(([resp, anonymousId]: [IQuickCheckoutReferralRequestResponse | null, string | null | undefined]) => {
      if (resp?.saga_id) {
        /* eslint-disable @typescript-eslint/naming-convention */
        this.authService.loginWithRedirect({
          authorizationParams: {
            scope: 'offline_access',
            screen_hint: 'signup',
            signup_type: 'quick_checkout',
            saga_id: resp?.saga_id,
            first_name: formValues.firstName,
            last_name: formValues.lastName,
            anonymous_id: anonymousId,
            eeNamespace: this.configurationService.env.eeNamespace
          }
        });
        /* eslint-enable @typescript-eslint/naming-convention */
      } else {
        this.quickCheckoutState = QuickCheckoutState.ERROR;
        this.resizeModalToFitContent();
      }
    });
  }

  private submitNonAuth0(formValues: IQuickCheckoutFormValues, isSessionTimeRequested: boolean): void {
    this.quickCheckoutStore.submitQuickCheckout$(
      formValues,
      this.requestedSessionPreferences,
      this.windowRef.nativeWindow?.location?.href ?? '',
      undefined,
      this.referringUrl,
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleSubmitError(error, isSessionTimeRequested)),
    ).subscribe((resp: IQuickCheckoutApiResponse | null) => {
      if (resp) {
        if (resp.referral_request_uuid) {
          this.quickCheckoutState = QuickCheckoutState.SUBMITTED;
        } else {
          this.quickCheckoutState = QuickCheckoutState.ERROR;
        }
        this.resizeModalToFitContent();
      }
    });
  }

  private handleSubmitError(error: HttpErrorResponse, isSessionTimeRequested: boolean): Observable<null> {
    if (error.status === TIME_SLOT_UNAVAILABLE_ERROR_CODE
        && error.message.toLocaleLowerCase().includes(TIME_SLOT_UNAVAILABLE_ERROR_MESSAGE)
        && isSessionTimeRequested
    ) {
      // Fetch new appointment slots before showing the calendar
      return this.providerCalendarStore.getAppointmentSlots$(this.provider.id, this.provider.timeZone, true).pipe(
        tap((slots) => {
          this.appointmentSlots = slots;
          this.resizeModalToFitContent();
          this.quickCheckoutState = QuickCheckoutState.SELECT_NEW_TIME;
        }),
        map(() => null)
      );
    }

    this.quickCheckoutState = QuickCheckoutState.ERROR;
    return of(null);
  }

  onClickResendEmail(): void {
    this.quickCheckoutStore.resendEmailVerification();
    this.sendSegmentEvent(
      SondermindEvent.ResendEmailClickedOnPslQuickCheckout,
      { personaProviderId: this.provider.id }
    );
  }

  onClickLinkToProviderDirectory(buttonClicked: string): void {
    const urlParams = new URLSearchParams(this.linkToProviderDirectory);
    const queryParams = {
      state: this.quickCheckoutFormGroup.controls.location.value ?? '', // this gets the state name, not abbrev
      insurance: urlParams.get('insurance') ?? '' // we want the string value from the url, not id
    };

    this.sendSegmentEvent(
      SondermindEvent.ProviderDirectoryLinkOnPslClicked,
      {
        buttonClicked,
        queryParams,
        currentProviderId: this.provider.id,
      }
    );
  }

  onClickSupportLink(): void {
    this.sendSegmentEvent(
      SondermindEvent.SupportLinkClickedOnPslQuickCheckoutSondermindcomContactus,
      { personaProviderId: this.provider.id }
    );
  }

  toggleShowAllInsuranceOptions(): void {
    this.showAllInsuranceOptions = !this.showAllInsuranceOptions;
  }

  private filterByInsuranceType(paymentPref: PaymentTypes): (insurance: IInsurance) => boolean {
    if (paymentPref === PaymentTypes.INSURANCE) {
      return (insurance: IInsurance) => insurance.isPrivateInsurance;
    }
    if (paymentPref === PaymentTypes.EAP) {
      return (insurance: IInsurance) => insurance.isEap;
    }
    if (paymentPref === PaymentTypes.MEDICARE) {
      return (insurance: IInsurance) => insurance.isMedicare;
    }
    if (paymentPref === PaymentTypes.MEDICAID) {
      return (insurance: IInsurance) => insurance.isMedicaid;
    }
    return (insurance: IInsurance) => insurance.isCashPay;
  }

  private getInsurancePlanLabel(paymentPref: PaymentTypes): string {
    return `Your 
      ${paymentPref === PaymentTypes.EAP ? 'EAP' : ''} 
      ${paymentPref === PaymentTypes.MEDICARE ? 'Medicare' : ''} 
      ${paymentPref === PaymentTypes.MEDICAID ? 'Medicaid' : ''} 
      ${paymentPref !== PaymentTypes.MEDICARE && paymentPref !== PaymentTypes.MEDICAID ? 'insurance' : ''} plan`;
  }

  private buildDirectoryLink(stateAbbreviation: string, insuranceId?: number): string {
    // TODO: refactor to DirectoryLinkService
    let insuranceTitle = '';
    if (insuranceId) {
      const insurance = this.insurances.find((i) => i.pncId === insuranceId)?.title;
      if (insurance) {
        insuranceTitle = encodeURIComponent(encodeSpecialCharacters(insurance.toLocaleLowerCase()));
      }
    }
    const insuranceLink = insuranceTitle ? `${URIPrefix.INSURANCE}-${insuranceTitle}` : '';
    const baseUrl = this.configurationService.env.providerDirectoryBase;
    return `${baseUrl}/${URIPrefix.STATE}-${stateAbbreviation.toLocaleLowerCase()}/`
      + `${URIPrefix.CITY}-${EmptyURIValue}/${insuranceLink}`;
  }

  private resizeModalToFitContent(): void {
    const cdkOverlay = document.querySelector('.iris-overlay-panel') as HTMLElement;
    if (cdkOverlay) {
      cdkOverlay.style.height = 'fit-content';
      cdkOverlay.style.width = 'fit-content';
    }
  }

  private sendSegmentEvent<T extends keyof SondermindEvents>(
    segmentEventType: T, data: SondermindEvents[T]
  ): void {
    from(this.segmentService.track<T>(segmentEventType, data)).pipe(take(1)).subscribe();
  }

  private compareStrings(str1: string, str2: string): boolean {
    return str1.localeCompare(str2) === 0;
  }

  private isAnAcceptedPaymentType(paymentType: PaymentTypes): boolean {
    return !!this.availablePaymentTypes.find((option) => option.value === paymentType);
  }

  /**
   * MER-4944: evaluate the OPC retest flag and fire exposure only when the user is a
   * real assignee of the experiment (variant key `opc` or `control`). null / off /
   * timeout / unknown are already filtered upstream in `request-a-session.service`
   * via `skipOpcExperiment: true`; this filter keeps QC exposure honest if that
   * upstream contract ever changes.
   */
  private trackOpcExperimentExposure(): void {
    // evaluate$ (Promise-derived, shareReplay(1)) and trackExposure$ (of(undefined)) both
    // emit once and complete on their own — no takeUntil/take(1) needed for cleanup.
    this.featureFlagService
      .evaluate$<string>(FeatureFlagKey.ONE_PAGE_CHECKOUT_EXPERIMENT_V2, '')
      .pipe(
        filter(isAssignedOpcVariant),
        switchMap((variant) => this.featureFlagService.trackExposure$(
          FeatureFlagKey.ONE_PAGE_CHECKOUT_EXPERIMENT_V2,
          variant,
        )),
      )
      .subscribe();
  }
}
